import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import type {
  NavMenuItemStoryblok,
  LinkItemStoryblok,
  NavDropdownStoryblok,
} from "../../types/global";
import { resolveLink } from "../../helpers/resolveLink";
import Button from "../../elements/Button";
import "./navigation.scss";

type LinkItemProps = {
  blok: LinkItemStoryblok;
  onLinkClick?: () => void;
};

const LinkItemLink = ({ blok, onLinkClick }: LinkItemProps) => {
  const { href, target } = resolveLink(blok.link);
  const isInternal = blok.link.linktype === "story";

  return (
    <Button
      link={href}
      target={target}
      label={blok.title}
      noIcon
      activeDot={isInternal}
      onClick={onLinkClick}
      className="nav-item"
    />
  );
};

type NavDropdownProps = {
  blok: NavDropdownStoryblok;
  onLinkClick?: () => void;
};

const NavDropdownMenu = ({ blok, onLinkClick }: NavDropdownProps) => {
  const [open, setOpen] = useState(false);
  const hasLink = Boolean(
    blok.link && (blok.link.cached_url || blok.link.url || blok.link.email)
  );

  // Na een klik blijft een link-titel focused, waardoor :focus-within de
  // hover-dropdown openhoudt totdat er ergens anders geklikt wordt. Focus
  // direct weer loslaten voorkomt dat "vastzittende" hover-state.
  const handleTitleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.blur();
    onLinkClick?.();
  };

  let title: JSX.Element;
  if (hasLink) {
    const { href, target } = resolveLink(blok.link!);
    const isInternal = blok.link!.linktype === "story";
    title = isInternal ? (
      <NavLink to={href} className="nav-dropdown-title" onClick={handleTitleClick}>
        {blok.title}
      </NavLink>
    ) : (
      <Link
        to={href}
        target={target}
        className="nav-dropdown-title"
        onClick={handleTitleClick}
      >
        {blok.title}
      </Link>
    );
  } else {
    title = (
      <p
        className="nav-dropdown-title"
        onClick={() => setOpen((prev) => !prev)}
      >
        {blok.title}
      </p>
    );
  }

  return (
    <div className={`nav-dropdown${open ? " open" : ""}`}>
      {title}
      <div className="nav-dropdown-items">
        <button
          type="button"
          className="nav-dropdown-close"
          onClick={() => setOpen(false)}
          aria-label="Sluiten"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
            <line x1="15" y1="1" x2="1" y2="15" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
        {blok.items?.map((item) => (
          <LinkItemLink
            blok={item}
            key={item._uid}
            onLinkClick={() => {
              setOpen(false);
              onLinkClick?.();
            }}
          />
        ))}
      </div>
    </div>
  );
};

type NavigationProps = {
  items: NavMenuItemStoryblok[];
  onLinkClick?: () => void;
  itemWrapper?: "li";
};

const Navigation = ({ items, onLinkClick, itemWrapper }: NavigationProps) => {
  return (
    <>
      {items.map((item) => {
        const rendered =
          item.component === "nav_dropdown" ? (
            <NavDropdownMenu
              blok={item}
              onLinkClick={onLinkClick}
              key={item._uid}
            />
          ) : (
            <LinkItemLink blok={item} onLinkClick={onLinkClick} key={item._uid} />
          );

        return itemWrapper === "li" ? (
          <li key={item._uid}>{rendered}</li>
        ) : (
          rendered
        );
      })}
    </>
  );
};

export default Navigation;
